import React, { useState, useEffect } from 'react';
import { Sidebar } from '../components/Sidebar';
import { PageHeader, LoadingSpinner, EmptyState } from '../components/UIComponents';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { usersAPI, sitesAPI, permissionsAPI } from '../utils/api';
import { Users, Mail, Shield, Plus, Edit, Trash2, Key, UserX, UserCheck, Building2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showPermissions, setShowPermissions] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'client',
    active: true,
    client_id: '',
  });
  
  const [newPassword, setNewPassword] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, sitesRes] = await Promise.all([
        usersAPI.getAll(),
        sitesAPI.getAll(),
      ]);
      setUsers(usersRes.data);
      setSites(sitesRes.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/auth/register`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User created successfully!');
      setShowAddUser(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to create user:', error);
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: formData.name,
        email: formData.email,
        role: formData.role,
        active: formData.active,
      };
      if (formData.client_id) {
        updateData.client_id = formData.client_id;
      }
      
      await axios.put(
        `${BACKEND_URL}/api/users/${selectedUser.user_id}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User updated successfully!');
      setShowEditUser(false);
      setSelectedUser(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Failed to update user:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/users/${selectedUser.user_id}/reset-password`,
        { new_password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Password reset successfully!');
      setShowResetPassword(false);
      setSelectedUser(null);
      setNewPassword('');
    } catch (error) {
      console.error('Failed to reset password:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleActive = async (user) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = user.active ? 'deactivate' : 'activate';
      await axios.put(
        `${BACKEND_URL}/api/users/${user.user_id}/${endpoint}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`User ${user.active ? 'deactivated' : 'activated'} successfully!`);
      fetchData();
    } catch (error) {
      console.error('Failed to toggle user status:', error);
      toast.error(error.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/users/${selectedUser.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('User deleted successfully!');
      setShowDeleteConfirm(false);
      setSelectedUser(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setSubmitting(false);
    }
  };

  const handleManagePermissions = async (user) => {
    setSelectedUser(user);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${BACKEND_URL}/api/permissions/user/${user.user_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUserPermissions(res.data);
      setShowPermissions(true);
    } catch (error) {
      console.error('Failed to fetch permissions:', error);
      toast.error('Failed to load permissions');
    }
  };

  const handleAddPermission = async (siteId) => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/permissions`,
        {
          user_id: selectedUser.user_id,
          site_id: siteId,
          can_view: true,
          can_download_reports: true,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Site access granted!');
      handleManagePermissions(selectedUser);
    } catch (error) {
      console.error('Failed to add permission:', error);
      toast.error('Failed to grant access');
    }
  };

  const handleRemovePermission = async (siteId) => {
    if (!selectedUser) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/permissions/${selectedUser.user_id}/${siteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Site access removed!');
      handleManagePermissions(selectedUser);
    } catch (error) {
      console.error('Failed to remove permission:', error);
      toast.error('Failed to remove access');
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role,
      active: user.active,
      client_id: user.client_id || '',
    });
    setShowEditUser(true);
  };

  const openResetPasswordModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setShowResetPassword(true);
  };

  const openDeleteModal = (user) => {
    setSelectedUser(user);
    setShowDeleteConfirm(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'client',
      active: true,
      client_id: '',
    });
  };

  const getRoleBadgeColor = (role) => {
    const colors = {
      super_admin: 'text-red-700 bg-red-50 border border-red-200',
      admin: 'text-orange-600 bg-orange-50',
      management: 'text-blue-600 bg-blue-50',
      engineering: 'text-violet-600 bg-violet-50',
      sales: 'text-emerald-600 bg-emerald-50',
      client: 'text-slate-600 bg-slate-50',
    };
    return colors[role] || 'text-slate-600 bg-slate-50';
  };

  const permittedSiteIds = userPermissions.map(p => p.site_id);
  const availableSites = sites.filter(s => !permittedSiteIds.includes(s.site_id));

  if (loading) {
    return (
      <div className="flex">
        <Sidebar />
        <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
          <LoadingSpinner />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <div className="flex-1 ml-0 lg:ml-64 pt-14 lg:pt-0">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <PageHeader
              title="User Management"
              description="Manage user accounts, roles, and site access permissions"
            />
            <Button
              onClick={() => { resetForm(); setShowAddUser(true); }}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="add-user-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </div>

          {users.length === 0 ? (
            <EmptyState
              icon={Users}
              title="No users found"
              description="User accounts will appear here"
            />
          ) : (
            <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
              <div className="overflow-x-auto">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.user_id} data-testid={`user-${user.user_id}`}>
                        <td className="font-medium text-slate-900">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                              <span className="text-slate-600 text-sm font-medium">
                                {user.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <span>{user.name}</span>
                          </div>
                        </td>
                        <td className="text-slate-600">
                          <div className="flex items-center space-x-2">
                            <Mail className="w-4 h-4 text-slate-400" />
                            <span>{user.email}</span>
                          </div>
                        </td>
                        <td>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium capitalize ${getRoleBadgeColor(user.role)}`}>
                            <Shield className="w-3 h-3 mr-1" />
                            {user.role}
                          </span>
                        </td>
                        <td>
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-sm text-xs font-medium ${
                            user.active ? 'text-green-600 bg-green-50' : 'text-slate-600 bg-slate-50'
                          }`}>
                            {user.active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="text-slate-600 text-sm">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                        <td>
                          <div className="flex items-center space-x-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openEditModal(user)}
                              title="Edit User"
                              data-testid={`edit-user-${user.user_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openResetPasswordModal(user)}
                              title="Reset Password"
                              data-testid={`reset-password-${user.user_id}`}
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            {user.role === 'client' && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleManagePermissions(user)}
                                title="Manage Site Access"
                                data-testid={`permissions-${user.user_id}`}
                              >
                                <Building2 className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleToggleActive(user)}
                              title={user.active ? 'Deactivate User' : 'Activate User'}
                              data-testid={`toggle-active-${user.user_id}`}
                            >
                              {user.active ? (
                                <UserX className="w-4 h-4 text-amber-600" />
                              ) : (
                                <UserCheck className="w-4 h-4 text-green-600" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteModal(user)}
                              title="Delete User"
                              className="text-red-600 hover:text-red-700"
                              data-testid={`delete-user-${user.user_id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddUser} className="space-y-4">
            <div>
              <Label htmlFor="name">Full Name</Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="John Doe"
              />
            </div>
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="john@example.com"
              />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div>
              <Label htmlFor="role">Role</Label>
              <Select value={formData.role} onValueChange={(v) => handleSelectChange('role', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="engineering">Engineering</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowAddUser(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? 'Creating...' : 'Create User'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={showEditUser} onOpenChange={setShowEditUser}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditUser} className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Full Name</Label>
              <Input
                id="edit-name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-email">Email</Label>
              <Input
                id="edit-email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                required
              />
            </div>
            <div>
              <Label htmlFor="edit-role">Role</Label>
              <Select value={formData.role} onValueChange={(v) => handleSelectChange('role', v)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="management">Management</SelectItem>
                  <SelectItem value="engineering">Engineering</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="client">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowEditUser(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={showResetPassword} onOpenChange={setShowResetPassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {selectedUser?.name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div>
              <Label htmlFor="new-password">New Password</Label>
              <Input
                id="new-password"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                placeholder="Minimum 6 characters"
              />
            </div>
            <div className="flex justify-end space-x-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setShowResetPassword(false)} disabled={submitting}>
                Cancel
              </Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? 'Resetting...' : 'Reset Password'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete User?</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{selectedUser?.name}</strong>? This action cannot be undone.
              All site permissions for this user will also be removed.
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={() => setShowDeleteConfirm(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button onClick={handleDeleteUser} disabled={submitting} className="bg-red-600 hover:bg-red-700">
              {submitting ? 'Deleting...' : 'Delete User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Manage Permissions Dialog */}
      <Dialog open={showPermissions} onOpenChange={setShowPermissions}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Manage Site Access</DialogTitle>
            <DialogDescription>
              Configure which sites {selectedUser?.name} can access
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Current Permissions */}
            <div>
              <Label className="text-sm font-medium">Current Access</Label>
              {userPermissions.length === 0 ? (
                <p className="text-sm text-slate-500 mt-2">No site access granted yet</p>
              ) : (
                <div className="mt-2 space-y-2">
                  {userPermissions.map((perm) => {
                    const site = sites.find(s => s.site_id === perm.site_id);
                    return (
                      <div key={perm.site_id} className="flex items-center justify-between p-2 bg-green-50 rounded-sm">
                        <div className="flex items-center space-x-2">
                          <Building2 className="w-4 h-4 text-green-600" />
                          <span className="text-sm">{site?.name || perm.site_id}</span>
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRemovePermission(perm.site_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Add New Permission */}
            {availableSites.length > 0 && (
              <div>
                <Label className="text-sm font-medium">Grant Access to Site</Label>
                <div className="mt-2 space-y-2">
                  {availableSites.map((site) => (
                    <div key={site.site_id} className="flex items-center justify-between p-2 bg-slate-50 rounded-sm">
                      <div className="flex items-center space-x-2">
                        <Building2 className="w-4 h-4 text-slate-400" />
                        <span className="text-sm">{site.name}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddPermission(site.site_id)}
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Grant
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => setShowPermissions(false)}>
              Done
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
